// src/components/brandCustomization/TicketCustomization/TicketHeroImageUpload.jsx
import React from 'react';
import { FaTimes } from 'react-icons/fa';
import Swal from 'sweetalert2';

/**
 * TicketHeroImageUpload - Componente para subir/eliminar imagen hero (solo para layout dark)
 */
const TicketHeroImageUpload = ({
    heroImage,
    marcaId,
    onUpload,
    onDelete,
    onRefresh,
    onFetchHeroImageOnly,
    onUpdateHeroImage
}) => {
    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const response = await onUpload(marcaId, file);

            // Actualizar solo el campo heroImage sin hacer refresh completo
            // Esto preserva los cambios locales del usuario (como el layout)
            if (onUpdateHeroImage) {
                if (response?.heroImage) {
                    // Si la respuesta incluye heroImage, usarlo directamente
                    onUpdateHeroImage(response.heroImage);
                } else if (onFetchHeroImageOnly) {
                    // Si no viene en la respuesta, obtener solo el heroImage del backend
                    await onFetchHeroImageOnly();
                } else {
                    // Último recurso: hacer refresh completo (puede perder cambios locales)
                    console.warn('No se pudo obtener heroImage de la respuesta, haciendo refresh completo');
                    if (onRefresh) await onRefresh();
                }
            } else if (onRefresh) {
                // Fallback: si no hay onUpdateHeroImage, hacer refresh completo
                await onRefresh();
            }

            Swal.fire({
                title: 'Imagen subida',
                text: 'La imagen hero se ha subido correctamente',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: error.message || 'No se pudo subir la imagen hero',
                icon: 'error'
            });
        }

        e.target.value = '';
    };

    const handleDelete = async () => {
        try {
            await onDelete(marcaId);

            // Actualizar solo el campo heroImage a null sin hacer refresh completo
            if (onUpdateHeroImage) {
                onUpdateHeroImage(null);
            } else if (onFetchHeroImageOnly) {
                // Obtener heroImage actualizado (debería ser null después de eliminar)
                await onFetchHeroImageOnly();
            } else if (onRefresh) {
                // Último recurso: hacer refresh completo
                await onRefresh();
            }

            Swal.fire({
                title: 'Imagen eliminada',
                text: 'La imagen hero se ha eliminado correctamente',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: error.message || 'No se pudo eliminar la imagen hero',
                icon: 'error'
            });
        }
    };

    return (
        <div className="mt-4 pt-4 border-t border-purple-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">
                Imagen Hero
            </label>
            <div className="space-y-2">
                {heroImage && (
                    <div className="relative">
                        <img
                            src={heroImage.cdnUrl || heroImage.url}
                            alt="Imagen hero actual"
                            className="w-full h-32 object-cover rounded-lg border border-gray-200"
                        />
                        <button
                            onClick={handleDelete}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                            title="Eliminar imagen"
                        >
                            <FaTimes className="w-3 h-3" />
                        </button>
                    </div>
                )}
                <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="w-full text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                />
                <p className="text-xs text-gray-500">
                    La imagen se usará como fondo hero en el layout Hero. Se recomienda una imagen de alta resolución (1920x1080px o superior).
                </p>
            </div>
        </div>
    );
};

export default TicketHeroImageUpload;

